import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configBusinessI18n } from './i18n';

const normalizeStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'inactive' || normalized === 'inactivo') return 'inactive';
  return 'active';
};

export default function BusinessCreateEditModal({
  Modal,
  Input,
  Dropdown,
  show,
  mode = 'create',
  value,
  onChange,
  onClose,
  onSave,
}) {
  const { language } = useLanguage();
  const businessLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configBusinessI18n });

  const isEdit = mode === 'edit';
  const [local, setLocal] = useState({
    id: null,
    name: '',
    description: '',
    status: 'active',
  });

  useEffect(() => {
    configBusinessI18n.changeLanguage(businessLanguage);
  }, [businessLanguage]);

  useEffect(() => {
    if (show) {
      setLocal({
        id: value?.id ?? null,
        name: value?.name ?? '',
        description: value?.description ?? '',
        status: normalizeStatus(value?.status),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, value?.id]);

  const nameValid = useMemo(() => local.name.trim().length > 0, [local.name]);
  const isValid = nameValid && ['active', 'inactive'].includes(local.status);

  const setField = (key) => (eventOrValue) => {
    const valueToSet = eventOrValue?.target
      ? eventOrValue.target.value
      : eventOrValue;
    setLocal((prev) => ({ ...prev, [key]: valueToSet }));
  };

  const handleSave = () => {
    if (!isValid) return;
    onChange?.(local);
    onSave?.(local);
  };

  if (!Modal || !Input) return null;

  const statusOptions = [
    { label: t('config_business_status_active'), value: 'active' },
    { label: t('config_business_status_inactive'), value: 'inactive' },
  ];
  const statusOptionFrom = (currentValue) =>
    statusOptions.find((option) => option.value === normalizeStatus(currentValue)) ||
    statusOptions[0];

  return (
    <Modal
      title={
        isEdit ? t('config_business_modal_title_edit') : t('config_business_modal_title_create')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        isEdit ? t('config_business_modal_primary_edit') : t('config_business_modal_primary_create')
      }
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid}
      secondaryBtnTxt={t('config_business_cancel')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='720px'
    >
      <div className='row g-3'>
        <div className='col-12'>
          <Input
            label={t('config_business_modal_name_label')}
            required
            placeholder={t('config_business_modal_name_placeholder')}
            value={local.name}
            onChange={setField('name')}
            error={
              local.name && !nameValid
                ? t('config_business_modal_name_required')
                : ''
            }
          />
        </div>

        <div className='col-12 col-md-6'>
          <label className='form-label'>{t('config_business_modal_status_label')}</label>
          {Dropdown ? (
            <Dropdown
              options={statusOptions}
              value={statusOptionFrom(local.status)}
              onChange={(option) =>
                setLocal((prev) => ({
                  ...prev,
                  status: option?.value || 'active',
                }))
              }
              placeholder={t('config_business_modal_status_placeholder')}
              width='100%'
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          ) : (
            <select
              className='form-select'
              value={local.status}
              onChange={setField('status')}
            >
              <option value='active'>{t('config_business_status_active')}</option>
              <option value='inactive'>{t('config_business_status_inactive')}</option>
            </select>
          )}
        </div>
      </div>
    </Modal>
  );
}
